'use client'

import { createContext, useContext, useMemo } from 'react';
import dayjs from 'dayjs';

import { IGlobalState, ITableMapMetadata, IAudienceMapMetadata } from './types';
import { useAuth } from '@/modules/auth/context/UserContext';
import { useDashboard } from '@/modules/dashboard/context/DashboardContext';
import { useWidgetData } from '@/app/widgets/WidgetFactoryContext';
import { Widget } from '@/modules';

// Default configurations
const DEFAULT_TABLE_MAP_CONFIG: ITableMapMetadata = {
  productIds: [1, 3, 14, 15],
  showTransactions: true,
};

const DEFAULT_AUDIENCE_MAP_CONFIG: IAudienceMapMetadata = {
  pageSize: 5000,
};

const initialValue: IGlobalState = {
  params: {
    token: '',
    userGuid: '',
    startDate: '',
    endDate: '',
    dealerId: '',
    dealerRefId: '',
    widgetId: '',
    product: '',
  },
  tableMapConfig: DEFAULT_TABLE_MAP_CONFIG,
  audienceMapConfig: DEFAULT_AUDIENCE_MAP_CONFIG,
}

const GlobalContext = createContext<IGlobalState>(initialValue);

const GlobalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mapToken, user } = useAuth();
  const { state: { selectedDealer, selectedDateRange, hydrated } } = useDashboard();
  const { widget, data } = useWidgetData();
  const widgetData = data as Widget;

  const params = useMemo<IGlobalState['params']>(() => ({
    token: mapToken || '',
    userGuid: user?.userId || '',
    startDate: widgetData?.service?.parameters?.startDate || widgetData?.service?.dataSources?.[0].parameters?.startDate || '',
    endDate: widgetData?.service?.parameters?.endDate || widgetData?.service?.dataSources?.[0].parameters?.endDate || '',
    dealerId: selectedDealer?.id?.toString() || '',
    dealerRefId: selectedDealer?.dealerRefId || '',
    widgetId: widget?.id?.toString() || '',
    product: widget?.metadata?.['table-map']?.product ?? '',
  }), [mapToken, user, selectedDealer, selectedDateRange, widget, widgetData]);

  const tableMapConfig = useMemo<ITableMapMetadata>(() => ({
    ...DEFAULT_TABLE_MAP_CONFIG,
    ...(widget?.metadata?.['table-map'] || {}),
  }), [widget?.metadata]);

  const audienceMapConfig = useMemo<IAudienceMapMetadata>(() => ({
    ...DEFAULT_AUDIENCE_MAP_CONFIG,
    ...(widget?.metadata?.['audience-map'] || {}),
  }), [widget?.metadata]);

  const isReady = hydrated && !!mapToken && !!selectedDealer?.dealerRefId && params.startDate && params.endDate;

  return (
    <GlobalContext.Provider value={{ params, tableMapConfig, audienceMapConfig }}>
      {isReady && children}
    </GlobalContext.Provider>
  )
}

function useGlobal() {
  return useContext(GlobalContext);
}

export {
  GlobalContext,
  GlobalProvider,
  useGlobal
}
